import type { NuxtError } from '#app'
import type { LoginForm, RegisterForm, AuthResponse } from '~/types/auth'

export const useStoreAuth = defineStore('auth', () => {
  const authCustomerToken = ref<string | null>(null)

  const isAuthenticated = computed(() => tokenInfo.value?.customer_id !== null && !isTokenExpired.value)

  /** @deprecated */
  const login = async (companyId: number, loginForm: LoginForm) => {
    const { email, password, token } = loginForm
    const res: AuthResponse = await useNuxtApp().$api(`/api-token-auth-creator/`, {
      method: 'POST',
      body: {
        company_id: companyId,
        email,
        password,
        'captcha-token': token
      }
    })
    return res
  }

  /** @deprecated */
  const register = async (companyId: number, registerForm: RegisterForm) => {
    const { email, password, token, firstname } = registerForm
    const res: AuthResponse = await useNuxtApp().$api(`/api/creators/`, {
      method: 'POST',
      body: {
        first_name: firstname,
        company_id: companyId,
        email,
        password,
        'captcha-token': token
      }
    })
    return res
  }

  const getJwtToken = () => {
    return authCustomerToken.value
  }

  const setJwtToken = (token: string) => {
    authCustomerToken.value = token
  }

  const tokenInfo = computed(() => {
    const token = authCustomerToken.value

    if (!token) return null

    return JSON.parse(atob(token.split('.')[1]))
  })

  const isTokenExpired = computed(() => {
    if (tokenInfo.value === null) return true

    return tokenInfo.value.exp < Date.now() / 1000
  })

  const refreshToken = async (companyId: number, customerId?: string | null) => {
    const body: { company_id: number; external_id?: string } = {
      company_id: companyId
    }

    if (customerId) {
      body.external_id = customerId
    }

    try {
      const refreshToken: AuthResponse = await useNuxtApp().$api(`/customers/jwt/`, {
        method: 'POST',
        body
      })
      authCustomerToken.value = refreshToken.token
    } catch (error) {
      const { statusCode } = error as NuxtError
      showError({ statusCode })
    }
  }

  const { setHasCustomerId } = useStoreStore()

  const authenticateFromStore = async (companyId: number, customerId?: string) => {
    let cust = null

    if (customerId && customerId !== 'undefined' && customerId !== 'null' && customerId !== '0') {
      cust = customerId
    }

    if (
      isTokenExpired.value ||
      (tokenInfo.value?.customer_id === null && cust !== null) ||
      (tokenInfo.value?.customer_id !== null && cust === null)
    ) {
      await refreshToken(companyId, cust)
      if (cust) setHasCustomerId(companyId, Number(cust))
    }
  }

  const logout = () => {
    const { companyId } = useStoreApp()
    refreshToken(companyId)
  }

  /** @deprecated */
  const requestNewPassword = async (companyId: number, email: string) => {
    return await useNuxtApp().$api(`/api/creator/password_forgotten/`, {
      method: 'POST',
      body: {
        email,
        company_id: companyId
      }
    })
  }

  /** @deprecated */
  const checkTokenNewPassword = async (token: string) => {
    await useNuxtApp().$api(`/api/creator/check_token_password_forgotten/?token_forgotten_password=${token}`)
  }

  /** @deprecated */
  const resetPassword = async (password: string, token: string) => {
    const { siteKey } = useRuntimeConfig().public.recaptcha
    return await useNuxtApp().$api(`/api/creator/password/`, {
      method: 'PUT',
      body: {
        password,
        token_forgotten_password: token,
        siteKey
      }
    })
  }

  return {
    login,
    logout,
    register,
    requestNewPassword,
    checkTokenNewPassword,
    resetPassword,
    isAuthenticated,
    getJwtToken,
    setJwtToken,
    refreshToken,
    tokenInfo,
    authenticateFromStore
  }
})
